import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import ProLayout from '@/components/layouts/ProLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { 
  LayoutDashboard, Users, BookOpen, UserCog, Video, BarChart3,
  Search, Filter, ChevronRight, User, Calendar, Clock, Award,
  TrendingUp, TrendingDown, Minus, Gamepad2, GraduationCap,
  Mail, Phone, Loader2, Eye, Calculator, FlaskConical, Languages,
  ClipboardList, FileText, PenTool
} from 'lucide-react';

const AdminStudentProgress = () => {
  const { user, api } = useAuth();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSubject, setFilterSubject] = useState('all');
  const [filterYearGroup, setFilterYearGroup] = useState('all');
  const [sortBy, setSortBy] = useState('last_activity');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [detailedProgress, setDetailedProgress] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const sidebarItems = [
    { path: '/admin', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/admin/users', label: 'User Management', icon: Users },
    { path: '/admin/allocate', label: 'Subject Allocation', icon: BookOpen },
    { path: '/admin/tutors', label: 'Tutor Assignment', icon: UserCog },
    { path: '/tutoring', label: 'Live Sessions', icon: Video },
    { path: '/admin/analytics', label: 'Analytics', icon: BarChart3 },
    { path: '/admin/student-progress', label: 'Student Progress', icon: TrendingUp },
    { path: '/admin/assessments', label: 'Assessments', icon: ClipboardList },
    { divider: true, label: 'Content Preview' },
    { path: '/admin/preview/games', label: 'Games Zone', icon: Gamepad2 },
    { path: '/admin/preview/exams', label: 'Exams', icon: FileText },
    { path: '/admin/preview/essays', label: 'Essays', icon: PenTool },
    { path: '/admin/preview/esol-videos', label: 'ESOL Videos', icon: Languages },
  ];

  useEffect(() => {
    fetchStudentsProgress();
  }, []);

  const fetchStudentsProgress = async () => {
    try {
      setLoading(true);
      const res = await api.get('/v2/analytics/admin/students-progress');
      setStudents(res.data.students || []);
    } catch (error) {
      console.error('Error fetching students progress:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStudentDetails = async (studentId) => {
    try {
      setLoadingDetails(true);
      const res = await api.get(`/v2/analytics/admin/student/${studentId}/detailed-progress`);
      setDetailedProgress(res.data);
    } catch (error) {
      console.error('Error fetching student details:', error);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleViewDetails = (student) => {
    setSelectedStudent(student);
    fetchStudentDetails(student.id);
  };

  const closeDetails = () => {
    setSelectedStudent(null);
    setDetailedProgress(null);
  };

  // Filter and sort students
  const filteredStudents = students
    .filter(s => {
      const matchesSearch = s.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           s.email?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesSubject = filterSubject === 'all' || 
                            s.allocated_subjects?.includes(filterSubject);
      const matchesYear = filterYearGroup === 'all' || 
                         s.year_group?.toString() === filterYearGroup;
      return matchesSearch && matchesSubject && matchesYear;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return (a.name || '').localeCompare(b.name || '');
        case 'lessons':
          return (b.progress?.total_lessons_completed || 0) - (a.progress?.total_lessons_completed || 0);
        case 'score':
          return (b.progress?.overall_average_score || 0) - (a.progress?.overall_average_score || 0);
        case 'last_activity':
        default:
          return (b.last_activity || '').localeCompare(a.last_activity || '');
      }
    });

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-emerald-600';
    if (score >= 60) return 'text-amber-600';
    return 'text-red-500';
  };

  const getScoreBg = (score) => {
    if (score >= 80) return 'bg-emerald-100';
    if (score >= 60) return 'bg-amber-100';
    return 'bg-red-100';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };

  const subjectIcons = {
    maths: Calculator,
    english: BookOpen,
    science: FlaskConical
  };

  if (loading) {
    return (
      <ProLayout title="Student Progress" sidebarItems={sidebarItems} user={user}>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
        </div>
      </ProLayout>
    );
  }

  return (
    <ProLayout title="Student Progress" sidebarItems={sidebarItems} user={user}>
      {/* Detail Modal */}
      {selectedStudent && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-emerald-600 to-teal-600 p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold">{selectedStudent.name}</h2>
                  <p className="text-emerald-100">Year {selectedStudent.year_group} • {selectedStudent.key_stage?.toUpperCase()}</p>
                </div>
                <Button 
                  variant="ghost" 
                  onClick={closeDetails}
                  className="text-white hover:bg-white/20"
                >
                  Close
                </Button>
              </div>
            </div>
            
            {/* Modal Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              {loadingDetails ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
                </div>
              ) : detailedProgress ? (
                <div className="space-y-6">
                  {/* Summary Cards */}
                  <div className="grid grid-cols-4 gap-4">
                    <div className="bg-emerald-50 rounded-xl p-4 text-center">
                      <p className="text-3xl font-bold text-emerald-600">{detailedProgress.summary?.total_lessons_completed || 0}</p>
                      <p className="text-sm text-emerald-700">Lessons Done</p>
                    </div>
                    <div className="bg-blue-50 rounded-xl p-4 text-center">
                      <p className="text-3xl font-bold text-blue-600">{detailedProgress.summary?.overall_average || 0}%</p>
                      <p className="text-sm text-blue-700">Avg Score</p>
                    </div>
                    <div className="bg-purple-50 rounded-xl p-4 text-center">
                      <p className="text-3xl font-bold text-purple-600">{detailedProgress.summary?.total_games_played || 0}</p>
                      <p className="text-sm text-purple-700">Games Played</p>
                    </div>
                    <div className="bg-amber-50 rounded-xl p-4 text-center">
                      <p className="text-3xl font-bold text-amber-600">{detailedProgress.student?.points || 0}</p>
                      <p className="text-sm text-amber-700">Total Points</p>
                    </div>
                  </div>

                  {/* Subject Breakdown */}
                  <div>
                    <h3 className="font-bold text-slate-800 mb-3">Subject Progress</h3>
                    <div className="grid md:grid-cols-3 gap-4">
                      {Object.entries(detailedProgress.summary?.subjects || {}).map(([subject, data]) => {
                        const Icon = subjectIcons[subject] || BookOpen;
                        return (
                          <div key={subject} className="bg-slate-50 rounded-xl p-4">
                            <div className="flex items-center gap-2 mb-3">
                              <Icon className="w-5 h-5 text-slate-600" />
                              <span className="font-semibold capitalize">{subject}</span>
                            </div>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-slate-500">Lessons</span>
                                <span className="font-medium">{data.total_lessons}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-slate-500">Average</span>
                                <span className={`font-medium ${getScoreColor(data.average_score)}`}>{data.average_score}%</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-slate-500">Topics</span>
                                <span className="font-medium">{data.topics_covered}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-slate-500">Last 7 days</span>
                                <span className="font-medium">{data.recent_lessons_7d} lessons</span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Recent Lessons */}
                  <div>
                    <h3 className="font-bold text-slate-800 mb-3">Recent Lessons</h3>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {detailedProgress.recent_lessons?.slice(0, 10).map((lesson, idx) => (
                        <div key={idx} className="flex items-center justify-between bg-slate-50 rounded-lg p-3">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${getScoreBg(lesson.percentage || lesson.score || 0)}`}>
                              <span className={`font-bold ${getScoreColor(lesson.percentage || lesson.score || 0)}`}>
                                {lesson.percentage || lesson.score || 0}%
                              </span>
                            </div>
                            <div>
                              <p className="font-medium text-slate-800 capitalize">{lesson.subject}</p>
                              <p className="text-xs text-slate-500">{lesson.topic_id?.replace(/-/g, ' ')}</p>
                            </div>
                          </div>
                          <span className="text-sm text-slate-500">{formatDate(lesson.completed_at)}</span>
                        </div>
                      ))}
                      {(!detailedProgress.recent_lessons || detailedProgress.recent_lessons.length === 0) && (
                        <p className="text-center text-slate-500 py-4">No lessons completed yet</p>
                      )}
                    </div>
                  </div>

                  {/* ESOL & Times Tables */}
                  <div className="grid md:grid-cols-2 gap-4">
                    {detailedProgress.esol_progress && (
                      <div className="bg-purple-50 rounded-xl p-4">
                        <h4 className="font-semibold text-purple-800 mb-2">ESOL Progress</h4>
                        <p className="text-sm text-purple-600">
                          {detailedProgress.esol_progress.completed_topics?.length || 0} topics completed
                        </p>
                      </div>
                    )}
                    {detailedProgress.times_tables_progress && (
                      <div className="bg-amber-50 rounded-xl p-4">
                        <h4 className="font-semibold text-amber-800 mb-2">Times Tables</h4>
                        <p className="text-sm text-amber-600">
                          {detailedProgress.times_tables_progress.mastered_tables?.length || 0} tables mastered
                          (Currently on: {detailedProgress.times_tables_progress.current_table || 1}x)
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <p className="text-center text-slate-500 py-12">No data available</p>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Student Progress</h1>
            <p className="text-slate-500">View and track all students' learning progress</p>
          </div>
          <div className="text-right">
            <p className="text-3xl font-bold text-emerald-600">{students.length}</p>
            <p className="text-sm text-slate-500">Total Students</p>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search by name or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              
              <select
                value={filterSubject}
                onChange={(e) => setFilterSubject(e.target.value)}
                className="px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
              >
                <option value="all">All Subjects</option>
                <option value="maths">Maths</option>
                <option value="english">English</option>
                <option value="science">Science</option>
              </select>
              
              <select
                value={filterYearGroup}
                onChange={(e) => setFilterYearGroup(e.target.value)}
                className="px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
              >
                <option value="all">All Years</option>
                {[1,2,3,4,5,6,7,8,9,10,11].map(y => (
                  <option key={y} value={y}>Year {y}</option>
                ))}
              </select>
              
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
              >
                <option value="last_activity">Last Active</option>
                <option value="name">Name A-Z</option>
                <option value="lessons">Most Lessons</option>
                <option value="score">Highest Score</option>
              </select>
            </div>
          </CardContent>
        </Card>

        {/* Student List */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600">Student</th>
                    <th className="text-left px-4 py-4 text-sm font-semibold text-slate-600">Year</th>
                    <th className="text-left px-4 py-4 text-sm font-semibold text-slate-600">Subjects</th>
                    <th className="text-center px-4 py-4 text-sm font-semibold text-slate-600">Lessons</th>
                    <th className="text-center px-4 py-4 text-sm font-semibold text-slate-600">Avg Score</th>
                    <th className="text-center px-4 py-4 text-sm font-semibold text-slate-600">Games</th>
                    <th className="text-left px-4 py-4 text-sm font-semibold text-slate-600">Last Active</th>
                    <th className="text-center px-4 py-4 text-sm font-semibold text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredStudents.map(student => (
                    <tr key={student.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                            <User className="w-5 h-5 text-emerald-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-800">{student.name}</p>
                            <p className="text-xs text-slate-500">{student.email}</p>
                            {student.parent && (
                              <p className="text-xs text-slate-400">Parent: {student.parent.name}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <span className="px-2 py-1 bg-slate-100 rounded text-sm font-medium">
                          Y{student.year_group}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex gap-1">
                          {student.allocated_subjects?.map(subj => (
                            <span 
                              key={subj}
                              className={`px-2 py-1 rounded text-xs font-medium capitalize ${
                                subj === 'maths' ? 'bg-amber-100 text-amber-700' :
                                subj === 'english' ? 'bg-rose-100 text-rose-700' :
                                'bg-emerald-100 text-emerald-700'
                              }`}
                            >
                              {subj.slice(0, 3)}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-4 text-center">
                        <span className="text-lg font-bold text-slate-800">
                          {student.progress?.total_lessons_completed || 0}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-center">
                        <span className={`text-lg font-bold ${getScoreColor(student.progress?.overall_average_score || 0)}`}>
                          {student.progress?.overall_average_score || 0}%
                        </span>
                      </td>
                      <td className="px-4 py-4 text-center">
                        <span className="text-lg font-medium text-purple-600">
                          {student.progress?.games_played || 0}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-slate-400" />
                          <span className={`text-sm ${student.last_activity ? 'text-slate-600' : 'text-slate-400'}`}>
                            {formatDate(student.last_activity)}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-center">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleViewDetails(student)}
                          className="flex items-center gap-1"
                        >
                          <Eye className="w-4 h-4" />
                          View
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {filteredStudents.length === 0 && (
                <div className="text-center py-12">
                  <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No students found matching your filters</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Summary Stats */}
        <div className="grid md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6 text-center">
              <p className="text-4xl font-bold text-emerald-600">
                {students.reduce((sum, s) => sum + (s.progress?.total_lessons_completed || 0), 0)}
              </p>
              <p className="text-sm text-slate-500 mt-1">Total Lessons Completed</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6 text-center">
              <p className="text-4xl font-bold text-blue-600">
                {students.length > 0 
                  ? Math.round(students.reduce((sum, s) => sum + (s.progress?.overall_average_score || 0), 0) / students.length)
                  : 0}%
              </p>
              <p className="text-sm text-slate-500 mt-1">Platform Average Score</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6 text-center">
              <p className="text-4xl font-bold text-purple-600">
                {students.reduce((sum, s) => sum + (s.progress?.games_played || 0), 0)}
              </p>
              <p className="text-sm text-slate-500 mt-1">Total Games Played</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6 text-center">
              <p className="text-4xl font-bold text-amber-600">
                {students.filter(s => {
                  if (!s.last_activity) return false;
                  const lastActive = new Date(s.last_activity);
                  const weekAgo = new Date();
                  weekAgo.setDate(weekAgo.getDate() - 7);
                  return lastActive >= weekAgo;
                }).length}
              </p>
              <p className="text-sm text-slate-500 mt-1">Active This Week</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </ProLayout>
  );
};

export default AdminStudentProgress;
